/* eslint-disable no-nested-ternary */
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ThemeProvider } from 'styled-components';
import _ from 'lodash';
import { InputContainer } from '../Input/styles';
import Select from '../Select/Select';
import { ThemeContext } from '../ThemeProvider/ThemeProvider';
import { SelectInputProps } from './types';

/**
 *
 * @param placeholder
 * @param labeled
 * @param options [{id, name}, ...] by default
 * @param keyProperty Name of the property asociated to 'id'. Default: 'id'
 * @param showProperty Name of the property asociated to label to show on each option. Default: 'name'
 * @param onChange
 * @param dropDownOnly If 'true' the behavior is like Select component
 * @param readOnly If 'true' the values cant be modified
 * @param disabled Its a boolean
 *
 */
const SelectInput: React.FC<SelectInputProps> = (props) => {
  const {
    input,
    options = [],
    disabled,
    keyProperty = 'id',
    showProperty = 'name',
    readOnly,
    dropDownOnly,
    isLoading,
    isValueObject,
    overlayPosition = 'auto',
    overAll = false,
    onChange,
    onMouseDown,
  } = props;

  const { actualTheme } = useContext(ThemeContext);
  const inputRef: React.Ref<HTMLInputElement> = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const optionSelected = useMemo(
    () => options.find((op) => _.get(op, keyProperty) === input?.value),
    [input?.value, keyProperty, options],
  );
  const [value, setValue] = useState(
    isValueObject
      ? _.get(input?.value, showProperty)
      : optionSelected && _.get(optionSelected, showProperty),
  );
  const [highlightedIndex, setHighlightedIndex] = useState({
    index: 0,
    id: '',
  });

  const handleClick = useCallback(() => {
    if (!disabled && !readOnly) {
      setIsOpen((prev) => !prev);
      if (onMouseDown) onMouseDown();
    }
  }, [disabled, onMouseDown, readOnly]);

  const handleChange = useCallback(
    (valueChange: any) => {
      if (input) input.onChange(isValueObject ? valueChange : _.get(valueChange, keyProperty));
      if (onChange) onChange(isValueObject ? valueChange : _.get(valueChange, keyProperty));
      setValue(_.get(valueChange, showProperty));
      setIsOpen(false);
    },
    [input, keyProperty, onChange, showProperty, isValueObject],
  );

  const clearInput = useCallback(() => {
    handleChange('');
  }, [handleChange]);

  useEffect(() => {
    if (!input.value && input.value !== 0) {
      setValue(isValueObject ? undefined : '');
    } else {
      setValue(
        isValueObject
          ? _.get(input?.value, showProperty)
          : optionSelected && _.get(optionSelected, showProperty),
      );
    }
  }, [input.value, isValueObject, optionSelected, showProperty]);

  return (
    <ThemeProvider theme={actualTheme}>
      <InputContainer id="select-id">
        <Select
          {...props}
          autoComplete="off"
          inputRef={inputRef}
          readOnly={readOnly || dropDownOnly}
          value={value}
          options={options}
          highlightedIndex={highlightedIndex}
          isOpen={isOpen}
          typingAllowed={false}
          showProperty={showProperty}
          keyProperty={keyProperty}
          disabled={disabled || isLoading}
          overlayPosition={overlayPosition}
          onChange={handleChange}
          onClear={clearInput}
          onMouseDown={handleClick}
          setHighlightedIndex={setHighlightedIndex}
          onSelection={handleChange}
          setIsOpen={setIsOpen}
          expand={handleClick}
          overAll={overAll}
        />
      </InputContainer>
    </ThemeProvider>
  );
};

export default SelectInput;
